import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';

const RiggingEditor = ({
  boatType,
  riggingConfig,
  onRiggingChange,
  onValidationChange,
  isStrictSidePreference,
  disabled = false,
}) => {
  const [localRigging, setLocalRigging] = useState({});

  // Initialize rigging configuration
  useEffect(() => {
    if (riggingConfig) {
      try {
        const parsed = JSON.parse(riggingConfig);
        setLocalRigging(parsed);
      } catch (error) {
        console.error('Failed to parse rigging config:', error);
        setLocalRigging({});
      }
    } else {
      setLocalRigging({});
    }
  }, [riggingConfig]);

  // Get boat type information - memoized to prevent unnecessary re-renders
  const boatInfo = useMemo(() => {
    const boatTypes = {
      '8+': { seats: 8, hasCox: true },
      '4+': { seats: 4, hasCox: true },
      '4-': { seats: 4, hasCox: false },
      '4x': { seats: 4, hasCox: false },
      '2+': { seats: 2, hasCox: true },
      '2-': { seats: 2, hasCox: false },
      '2x': { seats: 2, hasCox: false },
      '1x': { seats: 1, hasCox: false },
    };
    return boatTypes[boatType] || { seats: 0, hasCox: false };
  }, [boatType]);

  // Validate rigging configuration
  const validateRigging = (rigging) => {
    const portSeats = Object.values(rigging).filter((side) => side === 'port').length;
    const starboardSeats = Object.values(rigging).filter((side) => side === 'starboard').length;
    const neutralSeats = Object.values(rigging).filter((side) => side === 'neutral').length;

    // For strict side preference boats, all seats must be assigned (no neutral)
    const hasNeutralSeats = neutralSeats > 0;
    const hasEqualSides = portSeats === starboardSeats;
    const allSeatsAssigned = Object.keys(rigging).length === boatInfo.seats;

    return {
      isValid: hasEqualSides && !hasNeutralSeats && allSeatsAssigned,
      portSeats,
      starboardSeats,
      neutralSeats,
      totalConfigured: portSeats + starboardSeats + neutralSeats,
      hasNeutralSeats,
      allSeatsAssigned,
    };
  };

  // Handle rigging change for a specific seat - memoized to prevent unnecessary re-renders
  const handleRiggingChange = useCallback(
    (seatNumber, newSide) => {
      if (disabled) return;

      const newRigging = { ...localRigging };

      // Set the current seat
      newRigging[seatNumber] = newSide;

      // Apply alternating logic: when a seat is set to port/starboard,
      // all following seats alternate automatically
      if (newSide !== 'neutral') {
        let currentSide = newSide;
        for (let i = seatNumber + 1; i <= boatInfo.seats; i++) {
          currentSide = currentSide === 'port' ? 'starboard' : 'port';
          newRigging[i] = currentSide;
        }
      }

      setLocalRigging(newRigging);
      onRiggingChange(JSON.stringify(newRigging));
    },
    [disabled, localRigging, boatInfo.seats, onRiggingChange]
  );

  // Get rigging value for a seat - memoized to prevent unnecessary re-renders
  const getRiggingValue = useCallback(
    (seatNumber) => {
      return localRigging[seatNumber] || 'neutral';
    },
    [localRigging]
  );

  // Render rigging switch for a seat - memoized to prevent unnecessary re-renders
  const renderRiggingSwitch = useCallback(
    (seatNumber, label) => {
      const currentValue = getRiggingValue(seatNumber);

      return (
        <div
          key={seatNumber}
          className="flex items-center justify-between border-b border-gray-100 p-3"
        >
          <div className="flex items-center space-x-3">
            <span className="min-w-[60px] text-sm font-medium text-gray-700">{label}</span>
            <div className="flex items-center space-x-2">
              <button
                type="button"
                onClick={() => handleRiggingChange(seatNumber, 'port')}
                disabled={disabled}
                className={`rounded-md px-3 py-1 text-xs transition-colors ${
                  currentValue === 'port'
                    ? 'bg-red-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
              >
                Port
              </button>
              <button
                type="button"
                onClick={() => handleRiggingChange(seatNumber, 'neutral')}
                disabled={disabled || isStrictSidePreference}
                className={`rounded-md px-3 py-1 text-xs transition-colors ${
                  currentValue === 'neutral'
                    ? 'bg-gray-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                } ${disabled || isStrictSidePreference ? 'cursor-not-allowed opacity-50' : ''}`}
              >
                Neutral
              </button>
              <button
                type="button"
                onClick={() => handleRiggingChange(seatNumber, 'starboard')}
                disabled={disabled}
                className={`rounded-md px-3 py-1 text-xs transition-colors ${
                  currentValue === 'starboard'
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
              >
                Starboard
              </button>
            </div>
          </div>
        </div>
      );
    },
    [getRiggingValue, handleRiggingChange, disabled, isStrictSidePreference]
  );

  // Get validation status - memoized to prevent infinite re-renders
  const validation = useMemo(() => validateRigging(localRigging), [localRigging]);

  // Notify parent component of validation changes
  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(validation);
    }
  }, [validation, onValidationChange]);

  if (!boatType) {
    return (
      <div className="rounded-md border border-gray-200 bg-gray-50 p-4">
        <p className="text-sm text-gray-500">Please select a boat type to configure rigging.</p>
      </div>
    );
  }

  if (!isStrictSidePreference) {
    return (
      <div className="rounded-md border border-blue-200 bg-blue-50 p-4">
        <p className="mt-1 text-xs text-gray-600">
          Rigging configuration is only available for sweep boats (boats with strict side
          preference). This boat type allows flexible side assignments.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-md border border-gray-200 bg-white">
        {/* Render rower seats only (no cox) */}
        {Array.from({ length: boatInfo.seats }, (_, i) => i + 1).map((seatNumber) =>
          renderRiggingSwitch(seatNumber, `Seat ${seatNumber}`)
        )}
      </div>

      {/* Validation status */}
      <div
        className={`rounded-md border p-3 ${validation.isValid ? 'border-green-200 bg-green-50' : 'border-yellow-200 bg-yellow-50'}`}
      >
        <div className="flex items-center justify-between">
          <div className="text-sm">
            <span
              className={`font-medium ${validation.isValid ? 'text-green-800' : 'text-yellow-800'}`}
            >
              {validation.isValid ? '✓ Valid Configuration' : '⚠ Configuration Warning'}
            </span>
            <div className="mt-1 text-xs text-gray-600">
              Port: {validation.portSeats} | Starboard: {validation.starboardSeats} | Neutral:{' '}
              {validation.neutralSeats}
            </div>
          </div>
          {!validation.isValid && (
            <div className="text-xs text-yellow-700">
              {!validation.allSeatsAssigned && 'All seats must be assigned'}
              {validation.hasNeutralSeats && 'No neutral seats allowed for sweep boats'}
              {validation.allSeatsAssigned &&
                !validation.hasNeutralSeats &&
                validation.portSeats !== validation.starboardSeats &&
                (validation.portSeats > validation.starboardSeats
                  ? `Need ${validation.portSeats - validation.starboardSeats} more starboard seats`
                  : `Need ${validation.starboardSeats - validation.portSeats} more port seats`)}
            </div>
          )}
        </div>
      </div>

      <div className="text-xs text-gray-500">
        <p>
          • Setting a seat to Port or Starboard will automatically configure all following seats in
          alternating pattern
        </p>
        <p>
          • All seats must be assigned to either Port or Starboard (no neutral positions for sweep
          boats)
        </p>
        <p>• Cox position is not included in rigging configuration</p>
        <p>• Valid rigging requires equal numbers of port and starboard seats</p>
      </div>
    </div>
  );
};

RiggingEditor.propTypes = {
  boatType: PropTypes.string,
  riggingConfig: PropTypes.string,
  onRiggingChange: PropTypes.func.isRequired,
  onValidationChange: PropTypes.func,
  isStrictSidePreference: PropTypes.bool.isRequired,
  disabled: PropTypes.bool,
};

export default RiggingEditor;
